/**
 * Base Storage Adapter
 * Abstract base class defining the interface for all storage adapters
 */
class BaseStorageAdapter {
  /**
   * Upload a file to storage
   * @param {File} file - The file to upload
   * @param {string} folder - The folder to upload to (e.g., 'products', 'qr-codes')
   * @returns {Promise<string>} - The public URL of the uploaded file
   */
  async upload(file, folder) {
    throw new Error('upload() must be implemented by subclass');
  }

  /**
   * Delete a file from storage
   * @param {string} url - The URL of the file to delete
   * @returns {Promise<void>}
   */
  async delete(url) {
    throw new Error('delete() must be implemented by subclass');
  }

  /**
   * Get the public URL for a file path
   * @param {string} path - The file path
   * @returns {Promise<string>} - The public URL
   */
  async getUrl(path) {
    throw new Error('getUrl() must be implemented by subclass');
  }

  /**
   * Generate a unique file path
   * @param {File} file - The file to generate a path for
   * @param {string} folder - The folder to place the file in
   * @returns {string} - The unique file path (format: {YYYYMMDD}-{uuid}-{filename})
   */
  _generateUniquePath(file, folder) {
    // Generate timestamp in YYYYMMDD format
    const now = new Date();
    const timestamp = now.getFullYear() +
                     String(now.getMonth() + 1).padStart(2, '0') +
                     String(now.getDate()).padStart(2, '0');

    // Generate a short UUID (first 8 characters)
    const uuid = this._generateShortUUID();

    // Sanitize filename (remove special characters, keep extension)
    const sanitizedFilename = this._sanitizeFilename(file.name);

    // Combine into path: folder/YYYYMMDD-uuid-filename
    return `${folder}/${timestamp}-${uuid}-${sanitizedFilename}`;
  }

  /**
   * Generate a short UUID (8 characters)
   * @returns {string}
   */
  _generateShortUUID() {
    return 'xxxxxxxx'.replace(/x/g, () => {
      return Math.floor(Math.random() * 16).toString(16);
    });
  }

  /**
   * Sanitize a filename to remove special characters
   * @param {string} filename - The original filename
   * @returns {string} - The sanitized filename
   */
  _sanitizeFilename(filename) {
    // Get file extension
    const lastDot = filename.lastIndexOf('.');
    const name = lastDot > 0 ? filename.substring(0, lastDot) : filename;
    const ext = lastDot > 0 ? filename.substring(lastDot) : '';

    // Remove special characters, replace spaces with hyphens, lowercase
    const sanitized = name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    return sanitized + ext.toLowerCase();
  }
}

export default BaseStorageAdapter;
